// Sugalarm — the RECEIPT card's shelf life, SPA half (cycle 439 → 446).
//
// The green "🟢 RECOVERED — Back in range" card (cycle 369) is a RECEIPT,
// not a record: it announces the recovery and should then get out of the
// way. It was staying on the lock screen indefinitely — the operator
// screenshot that opened this cycle showed one still sitting there an hour
// after delivery.
//
// ⚠ THE SERVER CANNOT DO THIS. iOS gives a sender no way to expire a
// notification it has already delivered (`apns-expiration` bounds delivery
// RETRIES, not display; `dismissal-date` is a Live Activity key). A
// delivered card leaves the lock screen in exactly three ways: the user
// swipes it, a push with the same `apns-collapse-id` replaces it, or app
// code removes it. This module is the third one.
//
// This half runs only when the app is OPENED — it is the floor. The card
// clearing on its own, phone locked and untouched, is the native half
// (`RecoveredCardSweeper` on the cycle-356 silent push).
//
// ⚠ THE TWO HALVES READ DIFFERENT CLOCKS, DELIBERATELY. This half ages a
// card from the payload's `ts`; the native half ages it from
// `UNNotification.date`, the OS's own delivery stamp. The native one is
// strictly better where it is available (it cannot disagree with the
// "1h ago" the user is looking at), and the bridge does not expose a
// delivery stamp here. Both fail the same way — an age that cannot be
// established leaves the card alone — so the asymmetry can only ever make
// one half more conservative than the other, never make either remove a
// card it should have kept.
//
// ⚠ SAFETY — WHY THIS CAN NEVER TOUCH AN ALARM. The selector is a
// POSITIVE ALLOW-LIST of receipt kinds, and the alarm card carries no
// `kind` AT ALL — so it is unreachable by construction, not by exclusion.
// A card whose age cannot be established is left alone. Nothing here
// reads `level`, and nothing here can run on a card this app did not send.
//
// ⚠⚠ CYCLE 446 — `kind === "sensor_blackout"` IS CARRIED BY TWO OPPOSITE
// CARDS, and this is the whole design. The FEED-DARK PAGE is a CONDITION
// card (the feed is dark RIGHT NOW), it is `critical`/DND-bypassing, and
// cycle 443 gave it NO `expires_in` precisely so it can never expire while
// the sensor is still dark. The "DATA RESTORED" notice is a RECEIPT that
// lands in the SAME collapse slot (that is what makes it REPLACE the dark
// card) and DOES carry one. So `kind` alone cannot decide: every kind
// except `recovered` must carry an explicit positive `expires_in`, and its
// ABSENCE MEANS NEVER SWEEP. `recovered` keeps the fallback only for
// pre-439 cards already sitting on a device.
//
// ⚠ THIS FILE AND `ReceiptCardSweeper.swift` MUST AGREE ON THAT RULE.
// They read different clocks on purpose (see above) but the verdict logic
// is the same; changing one without the other is how a card gets removed
// on one path and kept on the other.

import {
  capacitorPlatform,
  getDeliveredNotifications,
  removeDeliveredNotifications,
  type DeliveredNotification,
} from "./capacitor";

/** Fallback shelf life, applied to `recovered` ONLY: 30 minutes, the
 *  shipped default of `SUGALARM_RECOVERED_CARD_TTL_SECONDS`.
 *
 *  ⚠ SCOPED TO ONE KIND ON PURPOSE. It exists so a RECOVERED card
 *  delivered before cycle 439 — already on a lock screen, carrying no
 *  `expires_in` — is still cleared. Extending it to the other kinds is
 *  exactly how the critical sensor-dark page would start expiring. */
export const DEFAULT_RECOVERED_TTL_SECONDS = 1800;

/** The POSITIVE allow-list. A card with no `kind` (every alarm card) or an
 *  unrecognised one is never swept.
 *
 *  ⚠ NEVER ADD A KIND THAT STATES A LIVE CONDITION. All four report
 *  something that ALREADY HAPPENED, which is what makes age a valid rule:
 *  the fact does not change while the card sits there. */
export const RECEIPT_KINDS: ReadonlySet<string> = new Set([
  "recovered",
  "sensor_blackout", // ⚠ the RESTORED notice only — see the header
  "reminder",
  "kiosk_notice",
]);

/** The shelf life to judge this card by, or `null` for "never sweep".
 *
 *  ⚠ ABSENT MEANS NEVER, FOR EVERY KIND BUT `recovered`. This one function
 *  is what keeps the critical sensor-DARK page on the lock screen. */
export function resolvedTtlSeconds(
  kind: string,
  declared: number | null,
): number | null {
  if (declared != null) return declared;
  return kind === "recovered" ? DEFAULT_RECOVERED_TTL_SECONDS : null;
}

function asString(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

/** `expires_in` arrives as a number over APNS and as a string over FCM. */
function ttlSecondsOf(data: Record<string, unknown>): number | null {
  const raw = data.expires_in;
  const n =
    typeof raw === "number"
      ? raw
      : typeof raw === "string" && raw.trim() !== ""
        ? Number(raw)
        : NaN;
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/**
 * PURE. Which delivered cards have outlived their shelf life at `nowMs`?
 *
 * A card is swept iff ALL of:
 *   - `data.kind` is in `RECEIPT_KINDS` — a positive allow-list;
 *   - `data.ts` parses (the ISO-8601 stamp the card was built with);
 *   - a shelf life RESOLVES (absent is fatal for every kind but
 *     `recovered` — see `resolvedTtlSeconds`);
 *   - it is > 0 (`0` means "never expire" — the server-side kill switch,
 *     and it must survive the trip to the client);
 *   - `nowMs - ts >= ttl`.
 *
 * Anything else — an alarm card, a reminder, a card with a garbled or
 * missing stamp, a card from the future (clock skew) — is left where it is.
 */
export function staleRecoveredIds(
  notifications: readonly DeliveredNotification[],
  nowMs: number,
): string[] {
  const out: string[] = [];
  for (const n of notifications) {
    const id = asString(n?.id);
    if (id == null) continue;
    const data = n?.data;
    if (data == null || typeof data !== "object") continue;
    const kind = asString((data as Record<string, unknown>).kind);
    if (kind == null || !RECEIPT_KINDS.has(kind)) continue;

    const ts = asString((data as Record<string, unknown>).ts);
    if (ts == null) continue;
    const sentAt = Date.parse(ts);
    if (!Number.isFinite(sentAt)) continue;

    const ttl = resolvedTtlSeconds(
      kind,
      ttlSecondsOf(data as Record<string, unknown>),
    );
    if (ttl == null || ttl <= 0) continue;

    if (nowMs - sentAt >= ttl * 1000) out.push(id);
  }
  return out;
}

/**
 * Clear every expired RECOVERED card. Best-effort in the strongest
 * sense: no throw escapes, and a failure leaves the card where it is.
 *
 * iOS ONLY. On Android the recovery card is posted by our own APK
 * (`StandardMessagingService.postRecoveryNotification`), not by the
 * plugin, so the bridge reports no `data` to select on — and Android
 * gets the OS-enforced `setTimeoutAfter`, which is strictly better than
 * a sweep-on-open anyway.
 */
export async function sweepRecoveredCards(
  now: number = Date.now(),
): Promise<number> {
  if (capacitorPlatform() !== "ios") return 0;
  try {
    const delivered = await getDeliveredNotifications();
    const stale = staleRecoveredIds(delivered, now);
    if (stale.length === 0) return 0;
    await removeDeliveredNotifications(stale);
    return stale.length;
  } catch (err) {
    console.warn("[recoveredCardSweep] sweep failed", err);
    return 0;
  }
}
